import { Component, Input, OnInit, OnChanges, SimpleChanges, inject, ViewChild, Output, EventEmitter } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {SignedLinkDirective} from 'src/app/directives/signed-link.directive';
import { DATE_TIME_FORMAT, MAX_FILE_SIZE } from 'src/app/shared/constants';
import {
    NotesClient, NoteDto, UploadedFileDto,
    CreateEmployeeNoteCommand, CreateAssetNoteCommand, CreateChecklistNoteCommand, CreateIncidentReportNoteCommand,
    ResultOfExportedFileDto,
    ResultOfListOfNoteDto,
    CreateAppointmentNoteCommand,
    CreateEmptyCheckInNoteCommand,
    CreateEmptyCheckOutNoteCommand,
    CreateInvoicePurchaseOrderNoteCommand,
    CreateInvoiceNoteCommand,
    CreateCreditRequestNoteCommand,
} from '@services';
import {
    DxButtonModule,
    DxPopupModule,
    DxTextAreaModule,
    DxFileUploaderModule,
    DxValidationGroupModule,
    DxValidationGroupComponent,
    DxValidatorModule,
    DxLoadPanelComponent,
    DxCheckBoxComponent
} from 'devextreme-angular';
import {DxiValidationRuleComponent} from 'devextreme-angular/ui/nested';
import notify from 'devextreme/ui/notify';
import {finalize} from 'rxjs/operators';
import {lastValueFrom, Observable} from "rxjs";
import {FileUtils} from '../../../shared/file-utils';

export enum NoteType {
    Employee = 'Employee',
    Asset = 'Asset',
    Checklist = 'Checklist',
    IncidentReport = 'IncidentReport',
    Appointment = 'Appointment',
    EmptyCheckIn = 'EmptyCheckIn',
    EmptyCheckOut = 'EmptyCheckOut',
    InvoicePurchaseOrder = 'InvoicePurchaseOrder',
    Invoice = 'Invoice',
    CreditRequest = 'CreditRequest',
}

@Component({
    selector: 'app-notes',
    templateUrl: './notes.component.html',
    styleUrls: ['./notes.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        DxButtonModule,
        DxPopupModule,
        DxTextAreaModule,
        DxFileUploaderModule,
        DxValidationGroupModule,
        DxValidatorModule,
        DxiValidationRuleComponent,
        DxLoadPanelComponent,
        SignedLinkDirective,
        DxCheckBoxComponent,
    ],
    providers: [DatePipe]
})
export class NotesComponent implements OnInit, OnChanges {
    @Input() entityId: string | null = null;
    @Input() noteType!: NoteType;
    @Input() canCreate = false;
    @Input() canCreateInternal = false;
    @Input() maxFileSize = MAX_FILE_SIZE;
    @Input() required = false;
    @Input() mode: 'normal' | 'invisible' = 'normal';

    @Output() noteSaved = new EventEmitter<void>();
    @Output() noteCanceled = new EventEmitter<void>();

    @ViewChild('validationGroup') validationGroup: DxValidationGroupComponent;

    private notesClient = inject(NotesClient);
    public dateTimeFormat = DATE_TIME_FORMAT;

    notes: NoteDto[] = [];
    isLoading = false;

    private queuedNotes: { body: string; uploadedFiles: UploadedFileDto[] }[] = [];

    isPopupVisible = false;
    newNoteBody = '';
    isInternal = false;
    newNoteFiles: File[] = [];

    ngOnInit(): void {
        this.loadNotes();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['entityId'] && !changes['entityId'].firstChange) {
            this.loadNotes();
        }
    }

    loadNotes() {
        if (!this.entityId || !this.noteType || this.mode === 'invisible') return;

        this.isLoading = true;
        let request: Observable<ResultOfListOfNoteDto>;

        if (this.noteType === NoteType.Employee) {
            request = this.notesClient.getEmployeeNotes(this.entityId);
        } else if (this.noteType === NoteType.Asset) {
            request = this.notesClient.getAssetNotes(this.entityId);
        } else if (this.noteType === NoteType.Checklist) {
            request = this.notesClient.getChecklistNotes(this.entityId);
        } else if (this.noteType === NoteType.IncidentReport) {
            request = this.notesClient.getIncidentReportNotes(this.entityId);
        } else if (this.noteType === NoteType.Appointment) {
            request = this.notesClient.getAppointmentNotes(this.entityId);
        } else if (this.noteType === NoteType.EmptyCheckIn) {
            request = this.notesClient.getEmptyCheckInNotes(this.entityId);
        } else if (this.noteType === NoteType.EmptyCheckOut) {
            request = this.notesClient.getEmptyCheckOutNotes(this.entityId);
        } else if (this.noteType === NoteType.InvoicePurchaseOrder) {
            request = this.notesClient.getInvoicePurchaseOrderNotes(this.entityId);
        } else if (this.noteType === NoteType.Invoice) {
            request = this.notesClient.getInvoiceNotes(this.entityId);
        } else if (this.noteType === NoteType.CreditRequest) {
            request = this.notesClient.getCreditRequestNotes(this.entityId);
        }

        if (request) {
            request.pipe(finalize(() => this.isLoading = false))
                .subscribe({
                    next: (res: any) => {
                        this.notes = (res.value || res.valueOrDefault || []).sort((a: NoteDto, b: NoteDto) => {
                            const timeA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
                            const timeB = b.lastModified ? new Date(b.lastModified).getTime() : 0;  
                            return timeB - timeA;
                        });
                    },
                    error: () => this.displayError('Failed to load notes')
                });
        } else {
            this.isLoading = false;
        }
    }

    public async saveQueuedNotes(entityId: string): Promise<void> {
        if (!this.queuedNotes.length) return;

        try {
            const promises = this.queuedNotes.map(note => {
                let request: Observable<string>;
                if (this.noteType === NoteType.Employee) {
                    request = this.notesClient.createEmployeeNote(new CreateEmployeeNoteCommand({
                        userId: entityId,
                        body: note.body,
                        uploadedFiles: note.uploadedFiles
                    }));
                } else if (this.noteType === NoteType.Asset) {
                    request = this.notesClient.createAssetNote(new CreateAssetNoteCommand({
                        assetId: entityId,
                        body: note.body,
                        uploadedFiles: note.uploadedFiles
                    }));
                } else if (this.noteType === NoteType.Checklist) {
                    request = this.notesClient.createChecklistNote(new CreateChecklistNoteCommand({
                        checklistId: entityId,
                        body: note.body,
                        uploadedFiles: note.uploadedFiles
                    }));
                } else if (this.noteType === NoteType.IncidentReport) {
                    request = this.notesClient.createIncidentReportNote(new CreateIncidentReportNoteCommand({
                        incidentReportId: entityId,
                        body: note.body,
                        uploadedFiles: note.uploadedFiles
                    }));
                } else if (this.noteType === NoteType.Appointment) {
                    request = this.notesClient.createAppointmentNote(new CreateAppointmentNoteCommand({
                        number: entityId,
                        body: note.body,
                        uploadedFiles: note.uploadedFiles
                    }));
                } else if (this.noteType === NoteType.EmptyCheckIn) {
                    request = this.notesClient.createEmptyCheckInNote(new CreateEmptyCheckInNoteCommand({
                        number: entityId,
                        body: note.body,
                        uploadedFiles: note.uploadedFiles
                    }));
                } else if (this.noteType === NoteType.EmptyCheckOut) {
                    request = this.notesClient.createEmptyCheckOutNote(new CreateEmptyCheckOutNoteCommand({
                        number: entityId,
                        body: note.body,
                        uploadedFiles: note.uploadedFiles
                    }));
                } else if (this.noteType === NoteType.InvoicePurchaseOrder) {
                    request = this.notesClient.createInvoicePurchaseOrderNote(new CreateInvoicePurchaseOrderNoteCommand({
                        number: entityId,
                        body: note.body,
                        uploadedFiles: note.uploadedFiles,
                        isInternal: this.isInternal
                    }));
                } else if (this.noteType === NoteType.Invoice) {
                    request = this.notesClient.createInvoiceNote(new CreateInvoiceNoteCommand({
                        number: entityId,
                        body: note.body,
                        uploadedFiles: note.uploadedFiles,
                        isInternal: this.isInternal
                    }));
                } else if (this.noteType === NoteType.CreditRequest) {
                    request = this.notesClient.createCreditRequestNote(new CreateCreditRequestNoteCommand({
                        number: entityId,
                        body: note.body,
                        uploadedFiles: note.uploadedFiles,
                        isInternal: this.isInternal
                    }));
                }

                return lastValueFrom(request);
            });

            await Promise.all(promises);
            this.queuedNotes = [];
        } catch (e) {
            console.error('Failed to save queued notes', e);
            throw e;
        }
    }

    exportNotes() {
        if (!this.entityId) return;
        let request: Observable<ResultOfExportedFileDto>;

        if (this.noteType === NoteType.Employee) {
            request = this.notesClient.exportEmployeeNotes(this.entityId);
        } else if (this.noteType === NoteType.Asset) {
            request = this.notesClient.exportAssetNotes(this.entityId);
        } else if (this.noteType === NoteType.Checklist) {
            request = this.notesClient.exportChecklistNotes(this.entityId);
        } else if (this.noteType === NoteType.IncidentReport) {
            request = this.notesClient.exportIncidentReportNotes(this.entityId);
        } else if (this.noteType === NoteType.Appointment) {
            request = this.notesClient.exportAppointmentNotes(this.entityId);
        } else if (this.noteType === NoteType.EmptyCheckIn) {
            request = this.notesClient.exportEmptyCheckInNotes(this.entityId);
        } else if (this.noteType === NoteType.EmptyCheckOut) {
            request = this.notesClient.exportEmptyCheckOutNotes(this.entityId);
        } else if (this.noteType === NoteType.InvoicePurchaseOrder) {
            request = this.notesClient.exportInvoicePurchaseOrderNotes(this.entityId);
        } else if (this.noteType === NoteType.Invoice) {
            request = this.notesClient.exportInvoiceNotes(this.entityId);
        } else if (this.noteType === NoteType.CreditRequest) {
            request = this.notesClient.exportCreditRequestNotes(this.entityId);
        }

        if (request) {
            request.subscribe({
                next: (res: any) => FileUtils.downloadFile(res.value || res.valueOrDefault),
                error: () => this.displayError('Failed to export notes')
            });
        }
    }

    public openCreatePopup()
    {
        this.isPopupVisible = true;
    }

    cancelNote() {
        this.isPopupVisible = false;
        this.noteCanceled?.emit();
    }

    async saveNote() {
        if (!this.validationGroup.instance.validate().isValid) return;

        if (this.newNoteFiles.some(file => file.size > this.maxFileSize)) {
            this.displayError(`One or more files exceed the ${this.maxFileSize / 1024 / 1024} MB size limit.`);
            return;
        }

        const uploadedFiles = await FileUtils.mapFilesToDto(this.newNoteFiles);

        if (!this.entityId) {
            this.queuedNotes.push({
                body: this.newNoteBody,
                uploadedFiles: uploadedFiles
            });

            const placeholderNote = new NoteDto({
                body: this.newNoteBody,
                lastModified: new Date(),
                attachments: this.newNoteFiles.map(f => ({ fileName: f.name, fileUrl: '#' } as any))
            });

            this.notes.unshift(placeholderNote);
            this.onPopupHidden();
            this.isPopupVisible = false;
            return;
        }

        let request: Observable<string>;

        if (this.noteType === NoteType.Employee) {
            request = this.notesClient.createEmployeeNote(new CreateEmployeeNoteCommand({
                userId: this.entityId,
                body: this.newNoteBody,
                uploadedFiles: uploadedFiles
            }));
        } else if (this.noteType === NoteType.Asset) {
            request = this.notesClient.createAssetNote(new CreateAssetNoteCommand({
                assetId: this.entityId,
                body: this.newNoteBody,
                uploadedFiles: uploadedFiles
            }));
        } else if (this.noteType === NoteType.Checklist) {
            request = this.notesClient.createChecklistNote(new CreateChecklistNoteCommand({
                checklistId: this.entityId,
                body: this.newNoteBody,
                uploadedFiles: uploadedFiles
            }));
        } else if (this.noteType === NoteType.IncidentReport) {
            request = this.notesClient.createIncidentReportNote(new CreateIncidentReportNoteCommand({
                incidentReportId: this.entityId,
                body: this.newNoteBody,
                uploadedFiles: uploadedFiles
            }));
        } else if (this.noteType === NoteType.Appointment) {
            request = this.notesClient.createAppointmentNote(new CreateAppointmentNoteCommand({
                number: this.entityId,
                body: this.newNoteBody,
                uploadedFiles: uploadedFiles
            }));
        } else if (this.noteType === NoteType.EmptyCheckIn) {
            request = this.notesClient.createEmptyCheckInNote(new CreateEmptyCheckInNoteCommand({
                number: this.entityId,
                body: this.newNoteBody,
                uploadedFiles: uploadedFiles
            }));
        } else if (this.noteType === NoteType.EmptyCheckOut) {
            request = this.notesClient.createEmptyCheckOutNote(new CreateEmptyCheckOutNoteCommand({
                number: this.entityId,
                body: this.newNoteBody,
                uploadedFiles: uploadedFiles
            }));
        } else if (this.noteType === NoteType.InvoicePurchaseOrder) {
            request = this.notesClient.createInvoicePurchaseOrderNote(new CreateInvoicePurchaseOrderNoteCommand({
                number: this.entityId,
                body: this.newNoteBody,
                uploadedFiles: uploadedFiles,
                isInternal: this.isInternal
            }));
        } else if (this.noteType === NoteType.Invoice) {
            request = this.notesClient.createInvoiceNote(new CreateInvoiceNoteCommand({
                number: this.entityId,
                body: this.newNoteBody,
                uploadedFiles: uploadedFiles,
                isInternal: this.isInternal
            }));
        } else if (this.noteType === NoteType.CreditRequest) {
            request = this.notesClient.createCreditRequestNote(new CreateCreditRequestNoteCommand({
                number: this.entityId,
                body: this.newNoteBody,
                uploadedFiles: uploadedFiles,
                isInternal: this.isInternal
            }));
        }

        if (request) {
            request.subscribe({
                next: () => {
                    notify('Note created successfully', 'success', 3000);
                    this.isPopupVisible = false;
                    this.loadNotes();
                    this.noteSaved?.emit();
                },
                error: (errors: any) => {
                    if (Array.isArray(errors)) {
                        errors.forEach((errMsg => {
                            this.displayError(errMsg.message);
                        }));
                    } else {
                        this.displayError('Failed to create note');
                    }
                }
            });
        }
    }

    private displayError(message: string) {
        notify({
            message: message,
            type: "error",
            displayTime: 5000,
        }, {
            position: "bottom right",
            direction: "up-push"
        });
    }

    onPopupHidden() {
        this.newNoteBody = '';
        this.isInternal = false;
        this.newNoteFiles = [];
        if (this.validationGroup) {
            this.validationGroup.instance.reset();
        }
        this.noteCanceled?.emit();
    }

    onFilesUploadChanged(e: any) {
        if (e.value && e.value.length > 5) {
            this.newNoteFiles = e.value.slice(0, 5);
            this.displayError('You can only upload a maximum of 5 files. The list has been truncated.');
        }
    }

    public validate(): boolean {
        return !(this.required && this.notes.length === 0);
    }
}
